import React, { useState, useRef, useEffect, useCallback, useId, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@core/ui';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SelectChangeHandler = (event: any) => void;

export interface SelectProps {
  value: string;
  onChange: SelectChangeHandler;
  options?: SelectOption[];
  children?: React.ReactNode;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  error?: string;
  helperText?: string;
  id?: string;
  required?: boolean;
  fullWidth?: boolean;
  onBlur?: () => void;
  autoFocus?: boolean;
  name?: string;
  /** Accessible name for dropdowns with no visible `label`. Forwarded to the trigger. */
  'aria-label'?: string;
  /** Native tooltip on the trigger. */
  title?: string;
}

function extractOptionsFromChildren(children: React.ReactNode): SelectOption[] {
  const options: SelectOption[] = [];
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === 'option') {
      options.push({
        value: String((child.props as any).value ?? ''),
        label: String((child.props as any).children ?? ''),
        disabled: !!(child.props as any).disabled,
      });
    }
  });
  return options;
}

export function Select({
  value,
  onChange,
  options: optionsProp,
  children,
  placeholder,
  className,
  disabled,
  label,
  error,
  helperText,
  id,
  required,
  fullWidth = true,
  onBlur,
  autoFocus,
  'aria-label': ariaLabel,
  title,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const generatedId = useId();
  const selectId = id || generatedId;
  const hasError = !!error;

  const childOptions = useMemo(
    () => (children ? extractOptionsFromChildren(children) : []),
    [children]
  );
  const options = optionsProp || childOptions;

  const selectedOption = options.find((o) => o.value === value);

  const fireChange = useCallback(
    (val: string) => {
      onChange({ target: { value: val } });
    },
    [onChange]
  );

  const close = useCallback(() => {
    setOpen(false);
    setFocusedIndex(-1);
  }, []);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const maxH = 240; // max-h-60 = 15rem = 240px
    const openAbove = spaceBelow < maxH && rect.top > spaceBelow;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      maxHeight: Math.min(maxH, openAbove ? rect.top - 8 : spaceBelow - 8),
    });
  }, []);

  useEffect(() => {
    if (autoFocus && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    updatePosition();

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        listRef.current && !listRef.current.contains(target)
      ) {
        close();
      }
    };

    const handleScroll = () => updatePosition();

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open, close, updatePosition]);

  useEffect(() => {
    if (open && listRef.current && focusedIndex >= 0) {
      const item = listRef.current.children[focusedIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          const idx = options.findIndex((o) => o.value === value);
          setFocusedIndex(idx >= 0 ? idx : 0);
        } else if (focusedIndex >= 0 && !options[focusedIndex]?.disabled) {
          fireChange(options[focusedIndex].value);
          close();
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          const idx = options.findIndex((o) => o.value === value);
          setFocusedIndex(idx >= 0 ? idx : 0);
        } else {
          setFocusedIndex((prev) => {
            let next = prev + 1;
            while (next < options.length && options[next].disabled) next++;
            return next < options.length ? next : prev;
          });
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (open) {
          setFocusedIndex((prev) => {
            let next = prev - 1;
            while (next >= 0 && options[next].disabled) next--;
            return next >= 0 ? next : prev;
          });
        }
        break;
    }
  };

  const handleSelect = (option: SelectOption) => {
    if (option.disabled) return;
    fireChange(option.value);
    close();
  };

  const dropdown = open
    ? createPortal(
        <ul
          ref={listRef}
          role="listbox"
          aria-activedescendant={
            focusedIndex >= 0 ? `${selectId}-option-${focusedIndex}` : undefined
          }
          style={dropdownStyle}
          className={cn(
            'z-50 overflow-auto rounded-md border py-1 shadow-lg',
            'bg-[hsl(var(--popover))] text-[hsl(var(--popover-foreground))] border-[hsl(var(--border))]'
          )}
        >
          {options.map((option, index) => {
            const isSelected = option.value === value;
            const isFocused = index === focusedIndex;
            return (
              <li
                key={option.value}
                id={`${selectId}-option-${index}`}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled}
                onMouseEnter={() => !option.disabled && setFocusedIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(option);
                }}
                className={cn(
                  'cursor-pointer select-none px-3 py-2 text-sm',
                  isFocused && 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]',
                  isSelected && !isFocused && 'bg-[hsl(var(--accent)/0.5)]',
                  option.disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {option.label}
              </li>
            );
          })}
          {options.length === 0 && (
            <li className="px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">
              No options
            </li>
          )}
        </ul>,
        document.body
      )
    : null;

  return (
    <div
      ref={containerRef}
      className={cn('relative', fullWidth && 'w-full', className)}
    >
      {label && (
        <label htmlFor={selectId} className="label">
          {label}
          {required && <span className="label-required" />}
        </label>
      )}
      <button
        ref={buttonRef}
        type="button"
        id={selectId}
        disabled={disabled}
        onBlur={onBlur}
        onClick={() => {
          if (!disabled) {
            setOpen((prev) => !prev);
            if (!open) {
              const idx = options.findIndex((o) => o.value === value);
              setFocusedIndex(idx >= 0 ? idx : 0);
            }
          }
        }}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={hasError}
        aria-label={ariaLabel}
        title={title}
        aria-describedby={
          hasError ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined
        }
        className={cn(
          'flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm text-left',
          'bg-[hsl(var(--background))] text-[hsl(var(--foreground))] border-[hsl(var(--input))]',
          'focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] focus:border-[hsl(var(--ring))]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          hasError && 'border-[hsl(var(--destructive))] focus:ring-[hsl(var(--destructive)/0.3)]'
        )}
      >
        <span className={cn(!selectedOption && 'text-[hsl(var(--muted-foreground))]')}>
          {selectedOption ? selectedOption.label : placeholder || '\u00A0'}
        </span>
        <svg
          className={cn(
            'ml-2 h-4 w-4 shrink-0 text-[hsl(var(--muted-foreground))] transition-transform',
            open && 'rotate-180'
          )}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {dropdown}

      {hasError && (
        <span id={`${selectId}-error`} className="input-error-text" role="alert">
          {error}
        </span>
      )}
      {!hasError && helperText && (
        <span id={`${selectId}-helper`} className="input-helper-text">
          {helperText}
        </span>
      )}
    </div>
  );
}
